import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getStoreValue } from 'pixel-react';

import privilegeApi from './privilegeApi';
import {
  Header,
  PrivilegeState,
  Restriction,
  PrivilegeDetailsTypes,
} from './privilegeApiTypes';

const defaultHeaders: Header[] = [
  { label: '', name: '', path: '', sections: [], subMenuItems: [] },
];

const defaultRestrictions: Restriction = {
  numberOfProjects: 0,
  numberOfUsers: 0,
  numberOfRoles: 0,
  allowedUsers: [],
  allowedProjects: [],
  numberOfElements: 0,
  numberOfProgramElements: 0,
  numberOfStepGroups: 0,
  numberOfScripts: 0,
  numberOfScriptsForModule: 0,
  numberOfSuites: 0,
  numberOfSuiteOfSuites: 0,
  numberOfDefects: 0,
};

const initialState: PrivilegeState = {
  global: {
    headers: defaultHeaders,
    restrictions: defaultRestrictions,
  },
  projectPrivilege: {},
  privilegeDetails: {},
};

const privilegeSlice = createSlice({
  name: 'privilege',
  initialState,
  reducers: {
    setHeaders(
      state,
      action: PayloadAction<{ key: string; headers: Header[] }>,
    ) {
      const { key, headers } = action.payload;

      if (key === 'global') {
        state.global.headers = headers;
      } else {
        if (!state.projectPrivilege[key]) {
          state.projectPrivilege[key] = {
            headers,
            restrictions: defaultRestrictions,
          };
        } else {
          state.projectPrivilege[key].headers = headers;
        }
      }
    },

    setRestrictions(
      state,
      action: PayloadAction<{ key: string; restrictions: Restriction }>,
    ) {
      const { key, restrictions } = action.payload;

      if (key === 'global') {
        state.global.restrictions = restrictions;
      } else {
        if (!state.projectPrivilege[key]) {
          state.projectPrivilege[key] = {
            headers: defaultHeaders,
            restrictions,
          };
        } else {
          state.projectPrivilege[key].restrictions = restrictions;
        }
      }
    },

    setPrivilegeDetails(state, action: PayloadAction<PrivilegeDetailsTypes>) {
      state.privilegeDetails = action.payload;
    },

    removePrivilegeEntry(state, action: PayloadAction<string>) {
      const key = action.payload;
      if (key !== 'global' && state.projectPrivilege[key]) {
        delete state.projectPrivilege[key];
      }
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      privilegeApi.endpoints.getMockPrivileges.matchFulfilled,
      (state, action: any) => {
        const { responseCode, responseObject } = action.payload;
        const currentProject = getStoreValue('current-project') as {
          id: string;
        };
        const projectId = currentProject?.id;
        if (responseCode === 200) {
          const isAllProjectData = responseObject?.projectLevels?.allProjects;
          const isIndividualProjectData =
            responseObject?.projectLevels?.individualProjects;
          // Full response (includes additionalKeys) for All Projects and individual.
          state.privilegeDetails = { ...responseObject };

          if (projectId === 'All Projects' && isAllProjectData) {
            state.global.headers = [...isAllProjectData.headers];
            state.global.restrictions = { ...isAllProjectData.restrictions };
          } else if (projectId && isIndividualProjectData) {
            if (!state.projectPrivilege[projectId]) {
              state.projectPrivilege[projectId] = {
                headers: [...isIndividualProjectData.headers],
                restrictions: { ...isIndividualProjectData.restrictions },
              };
            } else {
              state.projectPrivilege[projectId].headers = [
                ...isIndividualProjectData.headers,
              ];
              state.projectPrivilege[projectId].restrictions = {
                ...isIndividualProjectData.restrictions,
              };
            }
          }
        }
      },
    );
  },
});

export const {
  setHeaders,
  setRestrictions,
  removePrivilegeEntry,
  setPrivilegeDetails,
} = privilegeSlice.actions;

export const privilegeReducer = privilegeSlice.reducer;
